import { useAppSelector } from 'modules/Editor/store';
import { selectIsAllFontsLoaded } from 'modules/Editor/store/pageSelector';
import { useMemo } from 'react';
import { FlexLayout } from 'widgets/ResponsiveWidgets/LayoutComponents';
import { LabelTextWidgetData } from 'widgets/TextBasedWidgets/LabelTextWidget/LabelTextWidget.types';
import { useWidget } from 'widgets/sdk';

import { TEXT_ICON_GAP } from '../StatChartWidget.config';
import { calculateWidthFromFontSize, isLabelHidden } from '../StatChartWidget.helpers';
import { ComponentWidgetIdKeys, StatChartWidgetData } from '../StatChartWidget.types';
import { StatChartProps } from './StatCharts.types';

export const IconStatChart = ({ getWidgetMemberComponent, isReadOnly = false }: StatChartProps) => {
  const { componentWidgetIdMap, metricTextEnabled } = useWidget<StatChartWidgetData>();

  // Need to listen for when the fonts are loaded so that the correct width is calculated for label text
  const isAllFontsLoaded = useAppSelector(selectIsAllFontsLoaded);

  const iconGridWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.IconGrid];
  const labelTextWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.MetricText];
  const metricTextValue = useAppSelector(
    (state) => (state.infograph.widgets[labelTextWidgetId] as LabelTextWidgetData)?.value,
  );
  const labelTextWidgetStyle = useAppSelector(
    (state) => (state.infograph.widgets[labelTextWidgetId] as LabelTextWidgetData)?.style,
  );
  const labelTextIsHidden = useAppSelector(
    (state) => (state.infograph.widgets[labelTextWidgetId] as LabelTextWidgetData)?.isHidden,
  );
  const showLabelTextWidgetValue = useAppSelector((state) => {
    const showLabel = (state.infograph.widgets[labelTextWidgetId] as LabelTextWidgetData)?.showLabel;
    // for the one that already exists
    if (showLabel === undefined) return true;
    if (showLabel) return showLabel;
  });

  const IconGridWidget = getWidgetMemberComponent();
  const LabelTextWidget = getWidgetMemberComponent();
  const shapeFill = parseFloat(metricTextValue) || 0;

  const calculatedWidth = useMemo(
    () =>
      calculateWidthFromFontSize(
        metricTextValue,
        labelTextWidgetStyle.fontStyle,
        labelTextWidgetStyle.fontFamily,
        labelTextWidgetStyle.fontWeight,
        labelTextWidgetStyle.fontSize,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricTextValue, labelTextWidgetStyle, isAllFontsLoaded],
  );

  const customIconGridWidgetData = {
    shapeFill,
  };

  const customLabelTextWidgetData = {
    widthPx: calculatedWidth,
    isHidden: isLabelHidden({ metricTextEnabled, isWidgetHidden: labelTextIsHidden }),
    // the label for the statchart icon widget always has a font size option
    hasFontSizeOption: true,
  };

  return (
    <>
      <FlexLayout alignItems='center' gap={`${TEXT_ICON_GAP}px`} h='fit-content' w='fit-content'>
        {showLabelTextWidgetValue && (
          <LabelTextWidget
            widgetId={labelTextWidgetId}
            isReadOnly={isReadOnly}
            customWidgetData={customLabelTextWidgetData}
          />
        )}
        <FlexLayout gap={`${TEXT_ICON_GAP}px`} alignItems='center' w='fit-content'>
          <IconGridWidget
            customWidgetData={customIconGridWidgetData}
            widgetId={iconGridWidgetId}
            isReadOnly={isReadOnly}
          />
        </FlexLayout>
      </FlexLayout>
    </>
  );
};
